import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { Transition } from "../../pages/app/utils/animation";
import { intelligibilityRequest } from "../../pages/app/utils/dataLoader";
import { useModals } from "../../store/dialogs/hooks";
import { formatHHMMSSFromMs } from "../../pages/app/utils/formatDate";

export const StipaDialog = ({ singleChannelClips, clip }) => {
  const { clipDetailsClipId, showStipaDialog, closeStipaDialog } = useModals();
  const [selectedClip, setSelectedClip] = useState("");
  const [stoiableClips, setStoiableClips] = useState([]);

  const handleChange = (event) => setSelectedClip(event.target.value);

  const handleClose = () => {
    setSelectedClip("");
    closeStipaDialog();
  };

  const handleRun = async (requestType) => {
    console.log(
      `[STIPA/STOI] Requesting ${requestType} for clip: ${selectedClip}`,
    );
    await intelligibilityRequest(
      clip.caseId, // Assuming caseId is available in the context
      clip.id,
      selectedClip,
      requestType,
    );
    handleClose();
  };

  const unsuitableStoiError = (option) => {
    const { length, sampleRate } = clip;
    const errors = [];

    if (length < option.length) {
      errors.push(
        `Target length (${formatHHMMSSFromMs(option.length)}) must be less than audio to be scored (${formatHHMMSSFromMs(length)}).`,
      );
    }
    if (sampleRate !== option.sampleRate) {
      errors.push(
        `Target sample rate (${option.sampleRate}) does not match audio to be scored sample rate (${sampleRate}).`,
      );
    }

    return `Clip is not suitable for STOI calculation: ${errors.join(" ")}`;
  };

  useEffect(() => {
    setStoiableClips(
      singleChannelClips?.filter(
        //(item) => item.id !== clip.id && clip.alignedClipIds.includes(item.id),
        (item) => item.id !== clip.id,
      ),
    );
  }, [clip, singleChannelClips]);

  return (
    <Dialog
      aria-hidden={!showStipaDialog}
      open={showStipaDialog && clipDetailsClipId === clip.id}
      onClose={handleClose}
      fullWidth
      closeAfterTransition={false}
      slots={{
        transition: Transition,
      }}
    >
      <DialogTitle>STOI</DialogTitle>
      <DialogContent dividers>
        {Boolean(stoiableClips?.length) ? (
          <>
            <Typography gutterBottom>
              Select the test reference clip for the STOI calculation.
            </Typography>
            <Typography gutterBottom>
              Reference clips must be single channel.
            </Typography>
            <FormControl fullWidth margin="normal">
              <InputLabel id="stoi-select-label">Select a clip</InputLabel>
              <Select
                labelId="stoi-select-label"
                id="stoi-select"
                value={selectedClip}
                label="Select a clip"
                onChange={handleChange}
              >
                {stoiableClips?.map((item) => {
                  if (
                    item.sampleRate !== clip.sampleRate ||
                    item.length > clip.length
                  ) {
                    return (
                      <Tooltip
                        title={unsuitableStoiError(item)}
                        placement="top-start"
                        arrow
                        slotProps={{
                          popper: {
                            sx: {
                              "& .MuiTooltip-arrow": {
                                transform: "translateX(10px) !important",
                              },
                            },
                          },
                        }}
                      >
                        <span>
                          <MenuItem key={item.id} value={item.id} disabled>
                            <Typography sx={{ width: "100%" }}>
                              {item.description}
                            </Typography>
                          </MenuItem>
                        </span>
                      </Tooltip>
                    );
                  }
                  return (
                    <MenuItem key={item.id} value={item.id}>
                      {item.description}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </>
        ) : (
          <Box display="flex" alignItems="center" gap={1}>
            <InfoOutlined color="info" />
            <Typography>
              Please align a single channel reference clip before running STOI.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={() => handleRun("STOI")}
          disabled={!selectedClip}
          variant="contained"
        >
          Run STOI
        </Button>
      </DialogActions>
    </Dialog>
  );
};
